import { useCallback, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';
import { array, InferType, number, object, ObjectSchema, string } from 'yup';

import {
  AddressValue,
  AssetClass,
  BuildingReadModel,
  BuildingType,
  EnergyLabel,
  SetBuildingPackagePayload,
  SetBuildingPayload,
  StandardType,
  usePutBuildingsByIdMutation,
} from '@client/api';
import {
  addressValueSchema,
  AreaAdornment,
  Button,
  cadastralValueSchema,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Select,
  Skeleton,
  spaceSourceExternalReferenceValueSchema,
  TagAutocomplete,
  transformToOptionalNumber,
  Typography,
  YupRemoveIndex,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { BuildingAddressForm } from '../../components/building-form/building-address.form';
import { BuildingPackagesSection } from '../../components/building-form/building-packages-section';

const BUILDING_TYPE_OPTIONS: BuildingType[] = [
  'Office',
  'RetailHighStreet',
  'Retail',
  'Logistics',
  'Industrial',
  'Hotel',
  'Healthcare',
  'Education',
  'Mixed',
];

const STANDARD_TYPE_OPTIONS: StandardType[] = ['Low', 'Medium', 'High'];
const ENERGY_LABEL_OPTIONS: EnergyLabel[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

type FormModel = Omit<SetBuildingPayload, 'packages'>;

const schema: ObjectSchema<FormModel> = object({
  name: string().default(''),
  description: string().nullable(),
  address: addressValueSchema.nullable().makeOptional(),
  areaMetric: number().min(0).transform(transformToOptionalNumber),
  source: spaceSourceExternalReferenceValueSchema.nullable().makeOptional(),
  standard: string<StandardType>().nullable().default(undefined),
  type: string<BuildingType>().nullable().default(undefined),
  energyLabel: string<EnergyLabel>().nullable().default(undefined),
  heatingGrade: string().nullable().default(undefined),
  cadastralInformation: cadastralValueSchema.nullable().makeOptional(),
  // Byggnr is immutable and not edited here; kept in the schema for payload type compatibility
  // and left undefined so the API preserves the existing value on update.
  buildingNumber: string().nullable().default(undefined),
  operationalCostPriceId: string().nullable().default(undefined),
  tags: array().of(string().required()).nullable().default(undefined),
  assetClass: string<AssetClass>().nullable().default(undefined),
});

type FormValues = YupRemoveIndex<InferType<typeof schema>>;
type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

type BuildingFormData = FormValues & { packages?: SetBuildingPackagePayload[] | null };

interface BuildingEditInlineProps {
  building: BuildingReadModel | undefined;
  isFetching?: boolean;
  onAfterSubmit: () => void;
  onCancel: () => void;
}

export function BuildingEditInline({ building, isFetching = false, onAfterSubmit, onCancel }: BuildingEditInlineProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const [mutation] = usePutBuildingsByIdMutation();

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      name: building?.name ?? '',
      description: building?.description ?? '',
      address: building?.address ?? undefined,
      areaMetric: building?.area?.metric ?? ('' as any),
      source: building?.source ?? undefined,
      standard: building?.standard ?? undefined,
      type: building?.type ?? undefined,
      energyLabel: building?.energyLabel ?? undefined,
      heatingGrade: building?.heatingGrade ?? undefined,
      operationalCostPriceId: building?.operationalCost?.id ?? undefined,
      tags: building?.tags ?? undefined,
    }),
    [
      building?.address,
      building?.area?.metric,
      building?.description,
      building?.energyLabel,
      building?.heatingGrade,
      building?.name,
      building?.operationalCost?.id,
      building?.source,
      building?.standard,
      building?.type,
      building?.tags,
    ],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    watch,
    handleSubmit,
    setValue,
    reset,
  } = methods;

  const defaultPackages: SetBuildingPackagePayload[] = useMemo(() => {
    if (building?.packages && building.packages.length > 0) {
      return building.packages
        .filter((p) => p.isActive)
        .map((p) => ({ packageType: p.packageType, tier: p.tier ?? 'Basic' }));
    }
    return [];
  }, [building?.packages]);

  const [packages, setPackages] = useState<SetBuildingPackagePayload[]>(defaultPackages);

  const packagesChanged = useMemo(() => {
    if (packages.length !== defaultPackages.length) return true;
    return packages.some(
      (p, i) => p.packageType !== defaultPackages[i]?.packageType || p.tier !== defaultPackages[i]?.tier,
    );
  }, [packages, defaultPackages]);

  const selectedAddress = watch('address');
  const selectedTags = watch('tags');

  const [editingAddress, setEditingAddress] = useState(!selectedAddress);
  const handleToggleEditAddress = useCallback(() => setEditingAddress((current) => !current), []);

  const setAddressToFormValues = useCallback(
    (address: AddressValue | undefined = undefined) => {
      setValue('address', address, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  const setTagsToFormValues = useCallback(
    (e: CustomEvent<Tagify.ChangeEventData>) => {
      const tags = e.detail.tagify.getCleanValue().map((tag) => tag.value);
      setValue('tags', tags, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    },
    [setValue],
  );

  useDeepCompareEffect(() => {
    reset(defaultValues);
    setPackages(defaultPackages);
    const selectedAddress = watch('address');
    setEditingAddress(!selectedAddress);
  }, [defaultValues]);

  const handleFormSubmit = useCallback(
    async (data: BuildingFormData) => {
      if (building?.id == null) return;
      try {
        await mutation({ id: building.id, body: { ...data, packages } }).unwrap();
        onAfterSubmit();
      } catch {
        // error handled by RTK
      }
    },
    [building?.id, mutation, packages, onAfterSubmit],
  );

  return (
    <Card>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
          <CardHeader>
            <CardTitle className="flex-1" variant="highlighted">
              {ready ? tGlobal('edit.building') : <Skeleton variant="text" />}
            </CardTitle>
            <CardActions>
              <Button size="sm" color="primary-dark" onClick={onCancel}>
                {tCommon('cancel')}
              </Button>
              <FormSubmitButton size="sm" type="submit" disableDirtyHandling={packagesChanged} disabled={isFetching}>
                {tCommon('save')}
              </FormSubmitButton>
            </CardActions>
          </CardHeader>

          <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
            {/* Address */}
            <article className="pb-8">
              {!isFetching ? (
                <BuildingAddressForm
                  defaultValue={selectedAddress ?? undefined}
                  onChange={setAddressToFormValues}
                  editing={editingAddress}
                  onToggleEdit={handleToggleEditAddress}
                  isEditMode={!!building}
                />
              ) : (
                <Skeleton variant="rectangular" className="h-14" />
              )}
            </article>

            {/* Property information */}
            {selectedAddress && (
              <article className="pt-5 pb-8 border-t border-border space-y-4">
                <Typography variant="label" className="text-white">
                  {ready ? t('buildingCreate.propertyInformation') : <Skeleton variant="text" className="w-[40%]" />}
                </Typography>

                <GridRow cols={{ def: 1, sm: 2 }} spacingX={2} spacingY={4}>
                  <GridCol span={{ def: 1, sm: 2 }}>
                    <FormField<FormValues, FormPath>
                      name="name"
                      defaultValue={''}
                      label={tGlobal('building.name')}
                      loading={isFetching}
                      render={({ field }) => <Input {...field} data-1p-ignore />}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="type"
                      label={tGlobal('building.buildingType')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {BUILDING_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`BuildingType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="energyLabel"
                      label={tGlobal('building.energyLabel')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {ENERGY_LABEL_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="areaMetric"
                      label={tGlobal('building.area')}
                      loading={isFetching}
                      required
                      render={({ field }) => (
                        <AreaAdornment>
                          <Input type="number" className="pr-10" {...field} />
                        </AreaAdornment>
                      )}
                    />
                  </GridCol>

                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="standard"
                      label={tGlobal('space.standard')}
                      loading={isFetching}
                      render={({ field }) => (
                        <Select {...field}>
                          <option value="" />
                          {STANDARD_TYPE_OPTIONS.map((type) => (
                            <option key={type} value={type}>
                              {tType(`StandardType.${type}`)}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </GridCol>
                </GridRow>
              </article>
            )}

            {/* Tags */}
            {selectedAddress && (
              <article className="pt-5 pb-8 border-t border-border space-y-4">
                <GridRow cols={{ def: 1, sm: 2 }} spacingX={2} spacingY={4}>
                  <GridCol>
                    <FormField<FormValues, FormPath>
                      name="tags"
                      label={tGlobal('entity.tag')}
                      loading={isFetching}
                      render={() => (
                        <TagAutocomplete defaultValue={selectedTags ?? undefined} onChange={setTagsToFormValues} />
                      )}
                    />
                  </GridCol>
                </GridRow>
              </article>
            )}

            {/* Packages */}
            {selectedAddress && (
              <BuildingPackagesSection value={packages} onChange={setPackages} />
            )}
          </CardContent>
        </form>
      </FormProvider>
    </Card>
  );
}
